/**
 * RelationshipGraphModal — visual graph of entity relationships.
 */
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useAppStore } from '@/store/appStore';
import { X, ArrowRight } from 'lucide-react';

type Props = {
  open: boolean;
  onClose: () => void;
};

export function RelationshipGraphModal({ open, onClose }: Props) {
  const accountId = useAppStore((s) => s.currentAccountId) ?? undefined;
  const relationships = useQuery(api.entityRelationships.list, { accountId });
  const entities = useQuery(api.entities.list, { accountId });

  if (!open) return null;

  const entityMap = new Map(entities?.map((e: any) => [e._id, e]) ?? []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[80vh] rounded-xl border border-border bg-card shadow-xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="font-semibold">Entity relationships</h2>
          <button
            onClick={onClose}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {relationships === undefined ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : relationships.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No relationships yet. Add relationships between entities in the Entity Manager.
            </p>
          ) : (
            <ul className="space-y-2">
              {relationships.map((r: any) => {
                const from = entityMap.get(r.fromEntityId) as any;
                const to = entityMap.get(r.toEntityId) as any;
                return (
                  <li
                    key={`${r.fromEntityId}-${r.toEntityId}-${r.relationshipType}`}
                    className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2"
                  >
                    <span className="font-medium truncate">
                      {from?.displayName ?? from?.name ?? '?'}
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs px-1.5 py-0.5 rounded bg-muted">
                      {r.relationshipType}
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span className="font-medium truncate">
                      {to?.displayName ?? to?.name ?? '?'}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
